import { useState } from "react";
import { tableHead, tableRow } from '../../../utils/tableStyle';
import EmptyState from '../../CommonComponents/EmptyState';
import { Package } from 'lucide-react';
import PreReleaseModal from "../../CommonComponents/PreReleaseModal";

const PreReleaseHistoryTable = ({ history, loading }) => {
    // Sort by createdAt descending (newest first)
    const sortedHistory = [...history].sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA;
    });

    const [modalOpen, setModalOpen] = useState(false);
    const [selected, setSelected] = useState(null);

    const visibleHistory = sortedHistory.slice(0, 100);

    const openModal = (pr) => {
        setSelected(pr);
        setModalOpen(true);
    };
    const closeModal = () => {
        setModalOpen(false);
        setSelected(null);
    };

    return (
        <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden flex flex-col p-3 md:p-5 min-h-[500px]">
            <h3 className="font-semibold text-xs md:text-sm mb-2 text-brand-green tracking-tight uppercase letter-spacing-wide">4. My Pre-Releases</h3>
            <div className="rounded-lg border border-gray-200 overflow-hidden">
                <div className="max-h-[360px] overflow-y-auto">
                    <table className="w-full border-collapse text-xs">
                        <thead className={tableHead} style={{ background: 'var(--color-primary)' }}>
                            <tr>
                                <th className="px-2 py-2 text-center font-medium text-xs text-white" style={{ width: '25%' }}>Date</th>
                                <th className="px-2 py-2 text-center font-medium text-xs text-white" style={{ width: '25%' }}>Time</th>
                                <th className="px-2 py-2 text-center font-medium text-xs text-white" style={{ width: '25%' }}>Status</th>
                                <th className="px-2 py-2 text-center font-medium text-xs text-white" style={{ width: '25%' }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={4} className="px-3 py-4 text-center text-xs text-gray-500">Loading...</td></tr>
                            ) : visibleHistory.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="px-3 py-4 text-center">
                                        <EmptyState icon={Package} message="No pre-releases found." />
                                    </td>
                                </tr>
                            ) : (
                                visibleHistory.map(pr => {
                                    // Separate date and time
                                    let dateStr = "-";
                                    let timeStr = "-";
                                    if (pr.createdAt) {
                                        const dt = new Date(pr.createdAt);
                                        dateStr = dt.toLocaleDateString();
                                        timeStr = dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
                                    }
                                    return (
                                        <tr key={`${pr.id}-${pr.createdAt}`} className={tableRow + ' border-b last:border-0'}>
                                            <td className="px-2 py-2 text-xs text-gray-800 text-center" style={{ width: '25%' }}>{dateStr}</td>
                                            <td className="px-2 py-2 text-xs text-gray-800 text-center" style={{ width: '25%' }}>{timeStr}</td>
                                            <td className="px-2 py-2 text-xs text-gray-800 text-center" style={{ width: '25%' }}>
                                                <span className={`inline-block text-xs font-medium rounded-full px-2 py-0.5 ${pr.status === "APPROVED" ? "bg-green-100 text-green-600" :
                                                    pr.status === "REJECTED" ? "bg-red-100 text-red-600" :
                                                        "bg-yellow-100 text-yellow-800"
                                                    }`}>
                                                    {pr.status || "PENDING"}
                                                </span>
                                            </td>
                                            <td className="px-2 py-2 text-xs text-gray-800 text-center" style={{ width: '25%' }}>
                                                <button className="text-blue-600 hover:underline font-medium text-xs" onClick={() => openModal(pr)}>
                                                    View Details
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            {/* Modal for details */}
            <PreReleaseModal isOpen={modalOpen} onClose={closeModal} title={null}>
                {selected && (
                    <div className="space-y-4">
                        {/* Modal Title */}
                        <div className="text-center mb-2">
                            <h2 className="text-xl font-bold text-brand-green tracking-tight">Pre-Release Details</h2>
                            <div className="text-xs text-gray-400">
                                {selected && selected.createdAt ? (
                                    <>
                                        <span className="font-semibold">Date:</span> {new Date(selected.createdAt).toLocaleDateString()}<br />
                                        <span className="font-semibold">Time:</span> {new Date(selected.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                    </>
                                ) : '-'}
                            </div>
                        </div>
                        {/* Status and Remarks */}
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 border-b pb-2">
                            <div>
                                <span className="font-semibold text-gray-700">Status: </span>
                                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wide 
                                    ${selected.status === 'APPROVED' ? 'bg-green-100 text-green-700' : selected.status === 'REJECTED' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>{selected.status}</span>
                            </div>
                            <div>
                                <span className="font-semibold text-gray-700">Remarks: </span>
                                <span className="text-gray-600">{selected.remarks || <span className="italic text-gray-400">None</span>}</span>
                            </div>
                        </div>
                        {/* Items List */}
                        <div>
                            <div className="font-semibold text-brand-green mb-1">Products</div>
                            <ul className="divide-y divide-gray-100 rounded-lg border border-gray-100 bg-gray-50">
                                {selected.items?.map(item => (
                                    <li key={`${item.id}-${item.product_id}`}
                                        className="flex flex-col md:flex-row md:items-center gap-2 px-3 py-2">
                                        <div className="flex-1">
                                            <span className="font-medium text-gray-800">{item.product?.name || "Unknown"}</span>
                                            {item.product?.description && (
                                                <span className="block text-xs text-gray-500">{item.product.description}</span>
                                            )}
                                        </div>
                                        <div className="text-xs text-gray-700 font-semibold">Qty: {item.qty}</div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        {/* Photo */}
                        <div>
                            <div className="font-semibold text-brand-green mb-1">Photo</div>
                            {selected.photo_url ? (
                                <img src={selected.photo_url} alt="Pre-release" className="w-32 h-32 object-cover rounded border border-gray-200 mx-auto" />
                            ) : (
                                <div className="w-32 h-32 flex items-center justify-center bg-gray-100 rounded border border-gray-200 mx-auto">
                                    <svg className="w-10 h-10 text-gray-300" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                        <path d="M3 7V6a2 2 0 012-2h14a2 2 0 012 2v12a2 2 0 01-2 2H5a2 2 0 01-2-2v-1" />
                                        <circle cx="8.5" cy="8.5" r="1.5" />
                                        <path d="M21 15l-5-5L5 21" />
                                    </svg>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </PreReleaseModal>
        </div>
    );
};

export default PreReleaseHistoryTable;
